const SAFE_KEYS = [
  "--safe-area-inset-top",
  "--safe-area-inset-right",
  "--safe-area-inset-bottom",
  "--safe-area-inset-left",
] as const

type SafeAreaSide = "top" | "right" | "bottom" | "left"

const SIDE_BY_KEY: Record<(typeof SAFE_KEYS)[number], SafeAreaSide> = {
  "--safe-area-inset-top": "top",
  "--safe-area-inset-right": "right",
  "--safe-area-inset-bottom": "bottom",
  "--safe-area-inset-left": "left",
}

type LockedSafeAreas = Record<(typeof SAFE_KEYS)[number], string>

let lockedHeightPx = 0
let lockedSafeAreas: LockedSafeAreas | null = null
let styleObserver: MutationObserver | null = null

function measureEnvSafeAreaInset(side: SafeAreaSide): string {
  if (typeof document === "undefined") return "0px"

  const probe = document.createElement("div")
  probe.setAttribute("aria-hidden", "true")
  probe.style.cssText =
    "position:fixed;visibility:hidden;pointer-events:none;top:0;left:0;width:0;height:0;margin:0;border:0;"

  const cssSide =
    side === "top"
      ? "paddingTop"
      : side === "bottom"
        ? "paddingBottom"
        : side === "left"
          ? "paddingLeft"
          : "paddingRight"
  probe.style[cssSide] = `env(safe-area-inset-${side})`

  document.documentElement.appendChild(probe)
  const value = getComputedStyle(probe)[cssSide]?.trim() || "0px"
  probe.remove()

  return value
}

function readSafeAreaVar(name: (typeof SAFE_KEYS)[number]): string {
  const inline = document.documentElement.style.getPropertyValue(name).trim()
  if (inline) return inline

  const fromAttr = document.documentElement
    .getAttribute("style")
    ?.match(new RegExp(`${name}:\\s*([^;]+)`))?.[1]
    ?.trim()
  if (fromAttr) return fromAttr

  return measureEnvSafeAreaInset(SIDE_BY_KEY[name])
}

function applyLock() {
  const html = document.documentElement
  if (!lockedHeightPx) return

  html.style.setProperty("--cap-modal-viewport-h", `${lockedHeightPx}px`)
  html.setAttribute("data-capacitor-modal-locked", "")

  if (lockedSafeAreas) {
    for (const key of SAFE_KEYS) {
      html.style.setProperty(key, lockedSafeAreas[key])
    }
  }
}

function clearLock() {
  const html = document.documentElement
  html.removeAttribute("data-capacitor-modal-locked")
  html.style.removeProperty("--cap-modal-viewport-h")
  for (const key of SAFE_KEYS) {
    html.style.removeProperty(key)
  }
  styleObserver?.disconnect()
  styleObserver = null
  lockedHeightPx = 0
  lockedSafeAreas = null
}

/** Pin full-screen modal height + safe-area insets so keyboard resize does not reflow the dialog. */
export function lockModalViewport() {
  if (typeof document === "undefined") return

  lockedHeightPx = window.innerHeight
  lockedSafeAreas = {
    "--safe-area-inset-top": readSafeAreaVar("--safe-area-inset-top"),
    "--safe-area-inset-right": readSafeAreaVar("--safe-area-inset-right"),
    "--safe-area-inset-bottom": readSafeAreaVar("--safe-area-inset-bottom"),
    "--safe-area-inset-left": readSafeAreaVar("--safe-area-inset-left"),
  }

  applyLock()

  styleObserver?.disconnect()
  styleObserver = new MutationObserver(() => {
    if (!document.documentElement.hasAttribute("data-capacitor-modal-locked")) return
    applyLock()
  })
  styleObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["style"] })
}

export function unlockModalViewport() {
  if (typeof document === "undefined") return
  clearLock()
}

export function isModalViewportLocked(): boolean {
  return typeof document !== "undefined" && document.documentElement.hasAttribute("data-capacitor-modal-locked")
}
